import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Article } from "@/mocks/articles";
import AdminLayout from "@/components/feature/AdminLayout";
import MobileTableCards from "@/components/feature/MobileTableCards";
import { useArticlesDB } from "@/hooks/useArticlesDB";

const statusLabels: Record<Article["status"], string> = {
  draft: "Entwurf",
  pending_review: "Prüfung",
  published: "Veröffentlicht",
  scheduled: "Geplant",
  archived: "Archiviert",
};

const statusColors: Record<Article["status"], string> = {
  draft: "bg-gray-100 text-gray-700",
  pending_review: "bg-amber-100 text-amber-700",
  published: "bg-emerald-100 text-emerald-700",
  scheduled: "bg-sky-100 text-sky-700",
  archived: "bg-slate-100 text-slate-700",
};

const statusIcons: Record<Article["status"], string> = {
  draft: "ri-draft-line",
  pending_review: "ri-time-line",
  published: "ri-check-double-line",
  scheduled: "ri-calendar-schedule-line",
  archived: "ri-archive-line",
};

function MobileArticleCard({ article }: { article: Article }) {
  return (
    <div className="flex gap-3">
      <div className="w-16 h-14 shrink-0 rounded-md overflow-hidden" style={{ minWidth: "64px" }}>
        <img src={article.imageUrl} alt={article.title} loading="lazy" width={64} height={56} className="w-full h-full object-cover" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-hj-dark line-clamp-2 font-body">{article.title}</h3>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full ${statusColors[article.status]}`}>
            <i className={`${statusIcons[article.status]} text-[8px]`}></i>
            {statusLabels[article.status]}
          </span>
          <span className="text-[10px] text-hj-gray">{article.category}</span>
          <span className="text-[10px] text-hj-gray">{article.author}</span>
        </div>
        <div className="flex items-center gap-2 mt-1 text-[10px] text-hj-gray">
          <span>{new Date(article.publishedAt).toLocaleDateString("de-CH")}</span>
          <span>{article.views.toLocaleString("de-CH")} Aufrufe</span>
        </div>
      </div>
    </div>
  );
}

function MobileArticleActions({
  article,
  onPreview,
  onDelete,
}: {
  article: Article;
  onPreview: (slug: string) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <>
      <button
        onClick={() => onPreview(article.slug)}
        className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-hj-gray bg-hj-gray-light rounded-md hover:bg-hj-gray-border transition-colors"
      >
        <i className="ri-eye-line"></i>
        Vorschau
      </button>
      <Link
        to={`/admin/artikel-bearbeiten/${article.id}`}
        className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-hj-red border border-hj-red rounded-md hover:bg-hj-red hover:text-white transition-colors"
      >
        <i className="ri-edit-line"></i>
        Bearbeiten
      </Link>
      <button
        onClick={() => onDelete(article.id)}
        className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
      >
        <i className="ri-delete-bin-line"></i>
        Löschen
      </button>
    </>
  );
}

export default function AdminArticles() {
  const navigate = useNavigate();
  const { articles, loading, error, deleteArticle, bulkDelete, bulkUpdateStatus, refetch } = useArticlesDB();

  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterAuthor, setFilterAuthor] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [selectedArticles, setSelectedArticles] = useState<string[]>([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const showToast = (message: string, type: "success" | "error" = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const categories = [...new Set(articles.map((a) => a.categorySlug))];
  const allAuthors = [...new Set(articles.map((a) => a.authorSlug))];

  const filtered = articles.filter((a) => {
    const matchesSearch =
      a.title.toLowerCase().includes(search.toLowerCase()) ||
      a.author.toLowerCase().includes(search.toLowerCase()) ||
      a.excerpt.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !filterCategory || a.categorySlug === filterCategory;
    const matchesStatus = !filterStatus || a.status === filterStatus;
    const matchesAuthor = !filterAuthor || a.authorSlug === filterAuthor;
    return matchesSearch && matchesCategory && matchesStatus && matchesAuthor;
  });

  const stats = {
    total: articles.length,
    published: articles.filter((a) => a.status === "published").length,
    draft: articles.filter((a) => a.status === "draft").length,
    pending: articles.filter((a) => a.status === "pending_review").length,
    scheduled: articles.filter((a) => a.status === "scheduled").length,
    archived: articles.filter((a) => a.status === "archived").length,
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    const { error: err } = await deleteArticle(deleteId);
    setShowDeleteModal(false);
    setDeleteId(null);
    if (err) showToast(`Fehler: ${err}`, "error");
    else showToast("Artikel wurde gelöscht.");
  };

  const toggleSelect = (id: string) => {
    setSelectedArticles((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedArticles.length === filtered.length) setSelectedArticles([]);
    else setSelectedArticles(filtered.map((a) => a.id));
  };

  const handleBulkPublish = async () => {
    if (selectedArticles.length === 0) return;
    setBulkLoading(true);
    const count = selectedArticles.length;
    const { error: err } = await bulkUpdateStatus(selectedArticles, "published");
    setBulkLoading(false);
    setSelectedArticles([]);
    if (err) showToast(`Fehler: ${err}`, "error");
    else showToast(`${count} Artikel veröffentlicht.`);
  };

  const handleBulkArchive = async () => {
    if (selectedArticles.length === 0) return;
    setBulkLoading(true);
    const count = selectedArticles.length;
    const { error: err } = await bulkUpdateStatus(selectedArticles, "archived");
    setBulkLoading(false);
    setSelectedArticles([]);
    if (err) showToast(`Fehler: ${err}`, "error");
    else showToast(`${count} Artikel archiviert.`);
  };

  const handleBulkDelete = async () => {
    if (selectedArticles.length === 0) return;
    setBulkLoading(true);
    const count = selectedArticles.length;
    const { error: err } = await bulkDelete(selectedArticles);
    setBulkLoading(false);
    setSelectedArticles([]);
    if (err) showToast(`Fehler: ${err}`, "error");
    else showToast(`${count} Artikel gelöscht.`);
  };

  const activeFilterCount = [filterCategory, filterStatus, filterAuthor].filter(Boolean).length;

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5 md:mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-hj-dark font-heading">Artikel</h1>
            <p className="text-sm text-hj-gray font-body mt-1">{stats.total} Artikel insgesamt</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refetch}
              className="p-2.5 text-hj-gray border border-hj-gray-border rounded-md hover:bg-hj-gray-light transition-colors"
              title="Aktualisieren"
            >
              <i className="ri-refresh-line"></i>
            </button>
            <Link
              to="/admin/artikel-neu"
              className="px-4 py-2.5 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Neuer Artikel
            </Link>
          </div>
        </div>

        {/* Supabase error banner */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700 flex items-start gap-2">
            <i className="ri-error-warning-line shrink-0 mt-0.5"></i>
            <div>
              <strong>Supabase-Fehler:</strong> {error}
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-3 mb-5 md:mb-6">
          {[
            { label: "Gesamt", value: stats.total, color: "bg-hj-dark text-white", statusFilter: "" },
            { label: "Veröffentlicht", value: stats.published, color: "bg-emerald-50 text-emerald-700 border border-emerald-200", statusFilter: "published" },
            { label: "Entwürfe", value: stats.draft, color: "bg-gray-50 text-gray-700 border border-gray-200", statusFilter: "draft" },
            { label: "Prüfung", value: stats.pending, color: "bg-amber-50 text-amber-700 border border-amber-200", statusFilter: "pending_review" },
            { label: "Geplant", value: stats.scheduled, color: "bg-sky-50 text-sky-700 border border-sky-200", statusFilter: "scheduled" },
            { label: "Archiviert", value: stats.archived, color: "bg-slate-50 text-slate-700 border border-slate-200", statusFilter: "archived" },
          ].map((stat) => (
            <button
              key={stat.label}
              onClick={() => setFilterStatus(stat.statusFilter)}
              className={`p-2.5 md:p-3 rounded-lg text-left transition-all active:scale-[0.98] ${stat.color}`}
            >
              {loading ? (
                <div className="h-6 w-8 bg-current opacity-20 rounded animate-pulse mb-1"></div>
              ) : (
                <p className="text-lg md:text-xl font-bold">{stat.value}</p>
              )}
              <p className="text-[10px] md:text-xs opacity-80 mt-0.5 font-body">{stat.label}</p>
            </button>
          ))}
        </div>

        {/* Search + Filter Toggle */}
        <div className="bg-white rounded-lg border border-hj-gray-border p-3 md:p-4 mb-4 md:mb-6">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-hj-gray"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Artikel suchen..."
                className="w-full pl-9 pr-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red font-body"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium rounded-md border transition-colors whitespace-nowrap ${
                showFilters || activeFilterCount > 0
                  ? "border-hj-red text-hj-red bg-hj-red/5"
                  : "border-hj-gray-border text-hj-gray hover:bg-hj-gray-light"
              }`}
            >
              <i className="ri-filter-3-line"></i>
              <span className="hidden sm:inline">Filter</span>
              {activeFilterCount > 0 && (
                <span className="w-5 h-5 rounded-full bg-hj-red text-white text-[10px] flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {(search || filterCategory || filterStatus || filterAuthor) && (
              <button
                onClick={() => { setSearch(""); setFilterCategory(""); setFilterStatus(""); setFilterAuthor(""); }}
                className="px-3 py-2.5 text-sm text-hj-gray hover:text-hj-red transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            )}
          </div>

          {showFilters && (
            <div className="mt-3 pt-3 border-t border-hj-gray-border grid grid-cols-1 sm:grid-cols-3 gap-3">
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white font-body"
              >
                <option value="">Alle Kategorien</option>
                {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white font-body"
              >
                <option value="">Alle Status</option>
                {Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
              <select
                value={filterAuthor}
                onChange={(e) => setFilterAuthor(e.target.value)}
                className="px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white font-body"
              >
                <option value="">Alle Autoren</option>
                {allAuthors.map((auth) => <option key={auth} value={auth}>{auth}</option>)}
              </select>
            </div>
          )}
        </div>

        {/* Bulk Actions */}
        {selectedArticles.length > 0 && (
          <div className="bg-hj-gray-light rounded-lg p-3 mb-4 flex items-center justify-between">
            <span className="text-sm text-hj-dark font-medium font-body">{selectedArticles.length} ausgewählt</span>
            <div className="flex items-center gap-2">
              <button onClick={handleBulkPublish} disabled={bulkLoading} className="px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-md hover:bg-emerald-100 transition-colors disabled:opacity-50">Veröffentlichen</button>
              <button onClick={handleBulkArchive} disabled={bulkLoading} className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-50 rounded-md hover:bg-slate-100 transition-colors disabled:opacity-50">Archivieren</button>
              <button onClick={handleBulkDelete} disabled={bulkLoading} className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 transition-colors disabled:opacity-50">Löschen</button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg border border-hj-gray-border p-12 text-center">
            <div className="w-8 h-8 border-2 border-hj-red border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-sm text-hj-gray font-body">Artikel werden geladen...</p>
          </div>
        )}

        {/* Mobile Card View */}
        {!loading && (
          <div className="lg:hidden mb-4">
            <MobileTableCards
              items={filtered}
              keyExtractor={(a) => a.id}
              renderCard={(article) => <MobileArticleCard article={article} />}
              renderActions={(article) => (
                <MobileArticleActions
                  article={article}
                  onPreview={(slug) => navigate(`/artikel/${slug}`)}
                  onDelete={handleDelete}
                />
              )}
              emptyMessage="Keine Artikel gefunden."
              onSwipeLeft={(article) => handleDelete(article.id)}
              swipeLeftLabel="Löschen"
            />
          </div>
        )}

        {/* Desktop Table */}
        {!loading && (
          <div className="hidden lg:block bg-white rounded-lg border border-hj-gray-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-hj-gray-light">
                  <tr>
                    <th className="px-3 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={selectedArticles.length === filtered.length && filtered.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-hj-red rounded border-hj-gray-border focus:ring-hj-red"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">Artikel</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">Kategorie</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">Autor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">Datum</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">Aufrufe</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">Flags</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-hj-gray uppercase tracking-wider font-body">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-hj-gray-border">
                  {filtered.map((article) => {
                    const isSelected = selectedArticles.includes(article.id);
                    return (
                      <tr key={article.id} className={`hover:bg-gray-50 transition-colors ${isSelected ? "bg-hj-red/5" : ""}`}>
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(article.id)}
                            className="w-4 h-4 text-hj-red rounded border-hj-gray-border focus:ring-hj-red"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-10 rounded-md overflow-hidden shrink-0">
                              <img src={article.imageUrl} alt={article.title} loading="lazy" width={48} height={40} className="w-full h-full object-cover" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-hj-dark truncate max-w-[200px] font-body">{article.title}</p>
                              {article.subtitle && <p className="text-xs text-hj-gray truncate max-w-[200px] font-body">{article.subtitle}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 text-xs font-medium bg-hj-gray-light rounded-full text-hj-gray font-body">{article.category}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-hj-gray font-body">{article.author}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[article.status]}`}>
                            <i className={`${statusIcons[article.status]} text-[10px]`}></i>
                            {statusLabels[article.status]}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-hj-gray font-body">{new Date(article.publishedAt).toLocaleDateString("de-CH")}</td>
                        <td className="px-4 py-3 text-sm text-hj-gray font-body">{article.views.toLocaleString("de-CH")}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {article.isFeatured && <span className="w-5 h-5 flex items-center justify-center rounded bg-hj-red/10" title="Featured"><i className="ri-star-line text-hj-red text-xs"></i></span>}
                            {article.isBreaking && <span className="w-5 h-5 flex items-center justify-center rounded bg-red-50" title="Breaking"><i className="ri-flashlight-line text-red-500 text-xs"></i></span>}
                            {article.isTopNews && <span className="w-5 h-5 flex items-center justify-center rounded bg-amber-50" title="Top News"><i className="ri-arrow-up-line text-amber-500 text-xs"></i></span>}
                            {article.showOnHomepageSlider && <span className="w-5 h-5 flex items-center justify-center rounded bg-sky-50" title="Homepage Slider"><i className="ri-slideshow-line text-sky-500 text-xs"></i></span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => navigate(`/artikel/${article.slug}`)} className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-hj-gray-light transition-colors cursor-pointer" title="Vorschau">
                              <i className="ri-eye-line text-hj-gray"></i>
                            </button>
                            <Link to={`/admin/artikel-bearbeiten/${article.id}`} className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-hj-gray-light transition-colors" title="Bearbeiten">
                              <i className="ri-edit-line text-hj-gray"></i>
                            </Link>
                            <button onClick={() => handleDelete(article.id)} className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-red-50 transition-colors cursor-pointer" title="Löschen">
                              <i className="ri-delete-bin-line text-red-500"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filtered.length === 0 && !loading && (
              <div className="text-center py-12">
                <i className="ri-article-line text-4xl text-hj-gray-border mb-3 block"></i>
                <p className="text-hj-gray font-body">
                  {articles.length === 0 ? "Noch keine Artikel in Supabase vorhanden." : "Keine Artikel gefunden."}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-lg p-5 md:p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <i className="ri-alert-line text-red-500 text-lg"></i>
              </div>
              <h3 className="text-lg font-bold text-hj-dark font-heading">Artikel löschen?</h3>
            </div>
            <p className="text-sm text-hj-gray mb-6 font-body">Diese Aktion kann nicht rückgängig gemacht werden.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-2.5 text-sm font-medium text-hj-gray border border-hj-gray-border rounded-md hover:bg-gray-50 transition-colors">Abbrechen</button>
              <button onClick={confirmDelete} className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors">Löschen</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium font-body ${toast.type === "success" ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`}>
          <i className={toast.type === "success" ? "ri-check-line" : "ri-error-warning-line"}></i>
          {toast.message}
        </div>
      )}
    </AdminLayout>
  );
}
